import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bot, Send, Sparkles, Loader2, User as UserIcon, Plus, Mic, MicOff, Coins, Cpu, Wand2, Check, X, History, RotateCcw, CheckCheck, ShieldCheck, Lock, ChevronDown } from "lucide-react";

const SESSION_KEY = "groot_ai_session";
const newSession = () => `s${Date.now()}${Math.floor(Math.random() * 1000)}`;

const HIST_META = {
  applied: { label: "Uygulandı", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Reddedildi", cls: "bg-red-100 text-red-600" },
  reverted: { label: "Geri Alındı", cls: "bg-amber-100 text-amber-700" },
};

const ACT_META = {
  done: { label: "Yapıldı", cls: "bg-emerald-100 text-emerald-700" },
  blocked: { label: "Engellendi · Duvar", cls: "bg-red-100 text-red-600" },
  error: { label: "Hata", cls: "bg-amber-100 text-amber-700" },
};

const undoLeft = (s) => {
  if (!s.undo_deadline) return null;
  const ms = new Date(s.undo_deadline) - Date.now();
  if (ms <= 0) return { expired: true };
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return { expired: false, label: h >= 1 ? `${h}s ${m}dk` : `${m}dk` };
};

// Role/yetki bazlı hazır sorular — kısıtlı roller (örn. kasiyer) kâr/maliyet gibi soruları GÖRMEZ.
function buildSuggestions(can) {
  const s = [];
  if (can("report.read")) s.push("Bugün ne kadar satış yaptık?", "Son 30 günün en çok satan ürünleri neler?");
  else s.push("Bugünkü satışlarım nasıl gidiyor?", "Vardiyamda ne kadar tahsilat oldu?");
  s.push("SKT'si yaklaşan ürünleri listele", "Stoğu azalan ürünler neler?");
  if (can("purchase.read")) s.push("En kârlı ürünlerimiz hangileri?", "Hangi üründe tedarik fırsatı var?");
  if (can("price.read")) s.push("Piyasa ortalamasına göre fiyatlarımızı değerlendir");
  if (can("accounting.read")) s.push("Bu ayki gelir-gider dengemiz nasıl?");
  return s.slice(0, 6);
}

export default function AiAssistant() {
  const { can } = useAuth();
  const [cfg, setCfg] = useState(null);
  const suggestions = useMemo(() => buildSuggestions(can), [can]);
  const [sid, setSid] = useState(() => localStorage.getItem(SESSION_KEY) || newSession());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const canSuggest = can("price.write") || can("catalog.write");
  const [aiSugs, setAiSugs] = useState([]);
  const [sugBusy, setSugBusy] = useState(null);
  const [sugTab, setSugTab] = useState("pending");
  const [histSugs, setHistSugs] = useState([]);
  const [caps, setCaps] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [agentCfg, setAgentCfg] = useState(null);
  const [agentMode, setAgentMode] = useState(false);
  const [agentActions, setAgentActions] = useState([]);
  const agentCan = !!agentCfg?.can_use;
  const agentOn = agentCan && !!agentCfg?.enabled && agentMode;
  const [panelOpen, setPanelOpen] = useState(false);

  const loadPending = useCallback(() => api.get("/ai/suggestions?status=pending").then((r) => setAiSugs(r.data || [])).catch(() => {}), []);
  const loadHistory = useCallback(() => Promise.all(
    ["applied", "rejected", "reverted"].map((st) => api.get(`/ai/suggestions?status=${st}`).then((r) => r.data || []).catch(() => []))
  ).then((lists) => setHistSugs(lists.flat().sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))))), []);

  const loadAgentActions = useCallback(() => api.get("/ai/agent/actions").then((r) => setAgentActions(r.data || [])).catch(() => {}), []);

  useEffect(() => { localStorage.setItem(SESSION_KEY, sid); }, [sid]);
  useEffect(() => { api.get("/ai/assistant/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/ai/agent/config").then((r) => setAgentCfg(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!canSuggest) return;
    loadPending();
    api.get("/ai/capabilities").then((r) => setCaps(r.data)).catch(() => {});
  }, [canSuggest, loadPending]);
  useEffect(() => { if (canSuggest && sugTab === "history") loadHistory(); }, [canSuggest, sugTab, loadHistory]);
  useEffect(() => { if (agentCan && sugTab === "actions") loadAgentActions(); }, [agentCan, sugTab, loadAgentActions]);
  useEffect(() => { if (!canSuggest && agentCan) setSugTab("actions"); }, [canSuggest, agentCan]);
  useEffect(() => {
    api.get(`/ai/assistant/history?session_id=${sid}`).then((r) => setMessages(r.data || [])).catch(() => {});
  }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      if (agentMode) {
        const { data } = await api.post("/ai/agent", { session_id: sid, message: msg });
        setMessages((m) => [...m, { role: "assistant", content: data.reply, actions: data.actions }]);
        const done = (data.actions || []).filter((a) => a.status === "done").length;
        const blocked = (data.actions || []).filter((a) => a.status === "blocked").length;
        if (done) toast.success(`${done} işlem otomatik yapıldı`);
        if (blocked) toast.error(`${blocked} işlem Kesin Duvar tarafından engellendi`);
        if (data.credit_balance !== null && data.credit_balance !== undefined) setCfg((c) => (c ? { ...c, credit_balance: data.credit_balance } : c));
        if (sugTab === "actions") loadAgentActions();
      } else {
        const { data } = await api.post("/ai/assistant", { session_id: sid, message: msg });
        setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
        if (data.credit_balance !== null && data.credit_balance !== undefined) setCfg((c) => (c ? { ...c, credit_balance: data.credit_balance } : c));
      }
    } catch (err) {
      const detail = apiError(err.response?.data?.detail);
      const is429 = err?.response?.status === 429;
      toast.error(detail || "Yanıt alınamadı");
      setMessages((m) => [...m, { role: "assistant", content: is429 ? (detail || "AI günlük kullanım bütçesi doldu. Lütfen kontör/anahtar yükleyin.") : "Üzgünüm, şu an yanıt veremiyorum. Lütfen tekrar deneyin." }]);
    } finally { setBusy(false); }
  };

  const newChat = () => { const s = newSession(); setSid(s); setMessages([]); };

  const askSuggest = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/assistant/suggest", { session_id: sid, message: msg });
      const n = (data.suggestions || []).length;
      toast.success(n ? `${n} öneri üretildi — onayınıza sunuldu` : "Uygulanabilir öneri bulunamadı");
      setInput("");
      setSugTab("pending");
      loadPending();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Öneri üretilemedi");
    } finally { setBusy(false); }
  };

  const resolveSug = async (s, action) => {
    setSugBusy(s.id + action);
    try {
      await api.post(`/ai/suggestions/${s.id}/${action}`, {});
      toast.success(action === "apply" ? "Öneri uygulandı" : "Öneri reddedildi");
      setAiSugs((ls) => ls.filter((x) => x.id !== s.id));
      setSel((p) => { const n = new Set(p); n.delete(s.id); return n; });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setSugBusy(null);
  };

  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSel((s) => (s.size === aiSugs.length ? new Set() : new Set(aiSugs.map((x) => x.id))));

  const bulkResolve = async (action) => {
    if (!sel.size) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/ai/suggestions/bulk", { action, ids: [...sel] });
      toast.success(`${data.ok} öneri ${action === "apply" ? "uygulandı" : "reddedildi"}${data.failed ? ` · ${data.failed} başarısız` : ""}`);
      setSel(new Set());
      loadPending();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBulkBusy(false);
  };

  const undoSug = async (s) => {
    setSugBusy(s.id + "undo");
    try {
      await api.post(`/ai/suggestions/${s.id}/undo`, {});
      toast.success("Öneri geri alındı");
      loadHistory();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setSugBusy(null);
  };

  const undoAgentAction = async (a) => {
    setSugBusy(a.id + "aundo");
    try {
      await api.post(`/ai/agent/actions/${a.id}/undo`, {});
      toast.success("İşlem geri alındı");
      loadAgentActions();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setSugBusy(null);
  };

  const toggleMic = async () => {
    if (recording) { mediaRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      const mime = (window.MediaRecorder && cands.find((c) => MediaRecorder.isTypeSupported(c))) || "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const type = mr.mimeType || mime || "audio/webm";
        const ext = type.includes("mp4") ? "mp4" : "webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size < 800) return;
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, `ses.${ext}`);
          const { data } = await api.post("/ai/transcribe", fd);
          const text = (data.text || "").trim();
          if (text) send(text);
          else toast.error("Ses anlaşılamadı, tekrar deneyin");
        } catch (err) {
          toast.error(apiError(err.response?.data?.detail) || "Ses çevrilemedi");
        } finally { setTranscribing(false); }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Mikrofona erişilemedi (tarayıcı izni gerekli).");
    }
  };

  return (
    <div className="fade-in mx-auto flex h-[calc(100vh-9rem)] max-w-3xl flex-col" data-testid="ai-assistant-page">
      <div className="order-1 mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-green"><Bot className="h-5 w-5 text-white" /></div>
          <div><p className="overline">Yapay Zekâ</p><h1 className="font-heading text-2xl font-bold text-slate-900">AI Asistan</h1></div>
        </div>
        <div className="flex items-center gap-2">
          {cfg && (
            <>
              <span data-testid="ai-model-chip" className="hidden items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500 sm:inline-flex"><Cpu className="h-3 w-3 text-brand-green" /> {cfg.model}</span>
              <span data-testid="ai-credit-chip" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${cfg.unlimited ? "bg-amber-100 text-amber-700" : "bg-brand-green/10 text-brand-green"}`}>
                <Coins className="h-3 w-3" /> {cfg.unlimited ? "Sınırsız" : `${cfg.credit_balance ?? 0} kontör`}
              </span>
            </>
          )}
          <Button data-testid="ai-new-chat" onClick={newChat} size="sm" variant="outline"><Plus className="mr-1.5 h-4 w-4" /> Yeni Sohbet</Button>
        </div>
      </div>

      {(canSuggest || agentCan) && (
        <button data-testid="ai-panel-toggle" onClick={() => setPanelOpen((v) => !v)}
          className="order-2 mb-3 flex items-center justify-between gap-2 rounded-lg border border-brand-green/30 bg-brand-green/5 px-3 py-2 text-left transition-colors hover:bg-brand-green/10">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Wand2 className="h-4 w-4 text-brand-green" /> AI Önerileri &amp; Eylemler
            {canSuggest && aiSugs.length > 0 && <span data-testid="ai-panel-badge" className="rounded-full bg-brand-green px-1.5 py-0.5 text-[10px] font-bold text-white">{aiSugs.length} bekliyor</span>}
          </span>
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${panelOpen ? "rotate-180" : ""}`} />
        </button>
      )}

      <div className="order-4 flex-1 overflow-y-auto rounded-lg bg-white p-4 ring-1 ring-inset ring-slate-200">
        {messages.length === 0 && !busy && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <Sparkles className="mb-3 h-8 w-8 text-brand-orange" />
            <p className="font-heading text-lg font-bold text-slate-700">Verilerinize dair her şeyi sorun</p>
            <p className="mt-1 max-w-sm text-sm text-slate-500">Satış, stok, ciro ve SKT verilerinizi anlık analiz eder. Örneğin:</p>
            <p className="mt-2 max-w-sm rounded-md bg-slate-50 px-3 py-1.5 text-[11px] text-slate-400 ring-1 ring-inset ring-slate-100" data-testid="ai-readonly-note">Bu asistan yalnızca bilgi verir ve analiz yapar; sizin adınıza işlem yapmaz.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {suggestions.map((s, i) => (
                <button key={i} data-testid={`ai-suggestion-${i}`} onClick={() => send(s)}
                  className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-brand-green hover:text-white">{s}</button>
              ))}
            </div>
          </div>
        )}
        <div className="space-y-4">
          {messages.map((m, i) => (
            <div key={i} data-testid={`ai-message-${m.role}-${i}`} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${m.role === "user" ? "bg-brand-orange" : "bg-brand-green"}`}>
                {m.role === "user" ? <UserIcon className="h-4 w-4 text-white" /> : <Bot className="h-4 w-4 text-white" />}
              </div>
              <div className="flex max-w-[80%] flex-col gap-1.5">
                <div className={`whitespace-pre-wrap rounded-lg px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-brand-orange text-white" : "bg-slate-100 text-slate-800"}`}>{m.content}</div>
                {(m.actions || []).map((a, ai) => (
                  <div key={ai} data-testid={`ai-msg-action-${i}-${ai}`} className={`flex items-start gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] ${a.status === "done" ? "bg-emerald-50 text-emerald-800" : a.status === "blocked" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
                    {a.status === "done" ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> : a.status === "blocked" ? <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" /> : <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />}
                    <span><b>{a.label}:</b> {a.summary}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex gap-2.5" data-testid="ai-typing">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-green"><Bot className="h-4 w-4 text-white" /></div>
              <div className="rounded-lg bg-slate-100 px-3.5 py-2.5"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
            </div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      {(canSuggest || agentCan) && panelOpen && (
        <div data-testid="ai-suggestions-panel" className="order-3 mb-3 rounded-lg border border-brand-green/30 bg-brand-green/5 p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="inline-flex rounded-md bg-white p-0.5 ring-1 ring-inset ring-slate-200">
              {[
                ...(canSuggest ? [["pending", `Bekleyen (${aiSugs.length})`, Wand2], ["history", "Geçmiş", History]] : []),
                ...(agentCan ? [["actions", "Eylemler", Cpu]] : []),
                ["caps", "Yetkiler", ShieldCheck],
              ].map(([k, l, Icon]) => (
                <button key={k} data-testid={`ai-sug-tab-${k}`} onClick={() => setSugTab(k)}
                  className={`inline-flex items-center gap-1 rounded-sm px-2.5 py-1 text-xs font-semibold transition-colors ${sugTab === k ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-800"}`}>
                  <Icon className="h-3.5 w-3.5" /> {l}
                </button>
              ))}
            </div>
            {sugTab === "pending" && aiSugs.length > 0 && (
              <button data-testid="ai-sug-select-all" onClick={toggleSelectAll} className="text-[11px] font-semibold text-slate-500 hover:text-slate-800">{sel.size === aiSugs.length ? "Seçimi bırak" : "Tümünü seç"}</button>
            )}
          </div>

          {sugTab === "pending" && (
            <>
              {sel.size > 0 && (
                <div data-testid="ai-bulk-bar" className="mb-2 flex items-center gap-2 rounded-md bg-white px-2.5 py-1.5 ring-1 ring-inset ring-slate-200">
                  <span className="text-xs font-semibold text-slate-600">{sel.size} seçili</span>
                  <div className="ml-auto flex gap-1.5">
                    <Button data-testid="ai-bulk-apply" onClick={() => bulkResolve("apply")} disabled={bulkBusy} size="sm" className="h-7 bg-emerald-600 px-2.5 text-white hover:bg-emerald-700">{bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckCheck className="mr-1 h-3.5 w-3.5" /> Onayla</>}</Button>
                    <Button data-testid="ai-bulk-reject" onClick={() => bulkResolve("reject")} disabled={bulkBusy} size="sm" variant="outline" className="h-7 border-red-200 px-2.5 text-red-600 hover:bg-red-50"><X className="mr-1 h-3.5 w-3.5" /> Reddet</Button>
                  </div>
                </div>
              )}
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand-green"><Wand2 className="h-3.5 w-3.5" /> AI Önerileri — Onayınla Uygula</p>
              <div className="max-h-56 space-y-2 overflow-y-auto">
                {aiSugs.length === 0 && <p className="py-4 text-center text-xs text-slate-400">Bekleyen öneri yok. Girişteki soruya bir istek yazıp değnek (✨) butonuyla öneri üretebilirsiniz.</p>}
                {aiSugs.map((s) => (
                  <div key={s.id} data-testid={`ai-suggestion-card-${s.id}`} className="rounded-md bg-white p-2.5 ring-1 ring-inset ring-slate-200">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 items-start gap-2">
                        <input type="checkbox" data-testid={`ai-sug-check-${s.id}`} checked={sel.has(s.id)} onChange={() => toggleSel(s.id)} className="mt-1 h-4 w-4 accent-brand-green" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-800">{s.product_name} <span className="ml-1 rounded bg-slate-100 px-1 text-[10px] font-normal text-slate-500">{s.field === "price" ? "fiyat" : s.field}</span></p>
                          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-600"><span className="text-slate-400 line-through">{String(s.current ?? "—")}</span> → <span className="font-bold text-brand-green">{String(s.proposed)}</span></p>
                          {s.reason && <p className="mt-0.5 text-[11px] text-slate-400">{s.reason}</p>}
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button data-testid={`ai-sug-apply-${s.id}`} onClick={() => resolveSug(s, "apply")} disabled={!!sugBusy} size="sm" className="h-7 bg-emerald-600 px-2 text-white hover:bg-emerald-700">{sugBusy === s.id + "apply" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</Button>
                        <Button data-testid={`ai-sug-reject-${s.id}`} onClick={() => resolveSug(s, "reject")} disabled={!!sugBusy} size="sm" variant="outline" className="h-7 border-red-200 px-2 text-red-600 hover:bg-red-50"><X className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {sugTab === "history" && (
            <div className="max-h-56 space-y-2 overflow-y-auto" data-testid="ai-history-list">
              {histSugs.length === 0 && <p className="py-4 text-center text-xs text-slate-400">Geçmişte sonuçlanmış öneri yok.</p>}
              {histSugs.map((s) => {
                const st = HIST_META[s.status] || HIST_META.rejected;
                const ul = s.status === "applied" ? undoLeft(s) : null;
                return (
                  <div key={s.id} data-testid={`ai-history-card-${s.id}`} className="rounded-md bg-white p-2.5 ring-1 ring-inset ring-slate-200">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-800">{s.product_name} <span className="ml-1 rounded bg-slate-100 px-1 text-[10px] font-normal text-slate-500">{s.field === "price" ? "fiyat" : s.field}</span></p>
                        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-slate-600"><span className="text-slate-400 line-through">{String(s.current ?? "—")}</span> → <span className="font-bold text-slate-700">{String(s.proposed)}</span></p>
                        <span className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.cls}`}>{st.label}</span>
                      </div>
                      {s.status === "applied" && (ul?.expired ? (
                        <span data-testid={`ai-undo-expired-${s.id}`} className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-400">Süre doldu</span>
                      ) : (
                        <div className="flex shrink-0 flex-col items-end gap-0.5">
                          <Button data-testid={`ai-sug-undo-${s.id}`} onClick={() => undoSug(s)} disabled={!!sugBusy} size="sm" variant="outline" className="h-7 border-amber-300 px-2 text-amber-700 hover:bg-amber-50">{sugBusy === s.id + "undo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RotateCcw className="mr-1 h-3.5 w-3.5" /> Geri Al</>}</Button>
                          {ul?.label && <span data-testid={`ai-undo-left-${s.id}`} className="text-[9px] text-slate-400">{ul.label} kaldı</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {sugTab === "actions" && (
            <div className="max-h-56 space-y-2 overflow-y-auto" data-testid="ai-actions-list">
              <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-brand-orange"><Cpu className="h-3.5 w-3.5" /> AI Eylem Günlüğü — otonom yapılan işler</p>
              {agentActions.length === 0 && <p className="py-4 text-center text-xs text-slate-400">Henüz otonom işlem yok. Otonom modu (⚙) açıp bir istek yazın.</p>}
              {agentActions.map((a) => {
                const st = ACT_META[a.status] || { label: a.status, cls: "bg-slate-100 text-slate-500" };
                return (
                  <div key={a.id} data-testid={`ai-action-card-${a.id}`} className="rounded-md bg-white p-2.5 ring-1 ring-inset ring-slate-200">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800">{a.summary}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <span className="rounded bg-slate-100 px-1 text-[10px] font-normal text-slate-500">{a.label}</span>
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.cls}`}>{st.label}{a.undone ? " · geri alındı" : ""}</span>
                        </div>
                      </div>
                      {a.undoable && (
                        <Button data-testid={`ai-action-undo-${a.id}`} onClick={() => undoAgentAction(a)} disabled={!!sugBusy} size="sm" variant="outline" className="h-7 shrink-0 border-amber-300 px-2 text-amber-700 hover:bg-amber-50">{sugBusy === a.id + "aundo" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><RotateCcw className="mr-1 h-3.5 w-3.5" /> Geri Al</>}</Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {sugTab === "caps" && (
            <div className="max-h-56 space-y-3 overflow-y-auto" data-testid="ai-caps-panel">
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" /> AI şunları yapabilir</p>
                <div className="space-y-1">
                  {(caps?.allowed || []).map((a, i) => (
                    <div key={i} data-testid={`ai-cap-allowed-${i}`} className={`flex items-start gap-2 rounded-md px-2.5 py-1.5 text-xs ${a.enabled ? "bg-emerald-50 text-emerald-800" : "bg-slate-50 text-slate-400"}`}>
                      {a.enabled ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> : <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-300" />}
                      <span>{a.label}{a.tier === "approval" && <span className="ml-1 rounded bg-white px-1 text-[9px] font-semibold text-amber-600 ring-1 ring-inset ring-amber-200">onayınla</span>}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-red-600"><Lock className="h-3.5 w-3.5" /> ASLA erişemez — Kesin Duvar</p>
                <div className="space-y-1">
                  {(caps?.walled || []).map((w, i) => (
                    <div key={i} data-testid={`ai-cap-walled-${i}`} className="flex items-start gap-2 rounded-md bg-red-50/60 px-2.5 py-1.5 text-xs text-red-700">
                      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-400" /> <span>{w}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {agentOn && (
        <div data-testid="ai-agent-banner" className="order-5 mt-3 flex items-center gap-2 rounded-md bg-brand-orange/10 px-3 py-1.5 text-[11px] font-semibold text-brand-orange ring-1 ring-inset ring-brand-orange/20">
          <Cpu className="h-3.5 w-3.5 shrink-0" /> Otonom mod açık — AI düşük riskli işleri (stok sorgu, taslak sipariş, not) otomatik yapar; kritik alanlar Kesin Duvar ardındadır.
        </div>
      )}
      {agentCan && agentCfg && !agentCfg.enabled && (
        <div data-testid="ai-agent-disabled-note" className="order-5 mt-3 flex items-center gap-2 rounded-md bg-slate-100 px-3 py-1.5 text-[11px] font-medium text-slate-500 ring-1 ring-inset ring-slate-200">
          <Lock className="h-3.5 w-3.5 shrink-0" /> Otonom asistan yönetici tarafından kapatılmış. Ayarlar → Entegrasyonlar → AI Asistan'dan açabilirsiniz.
        </div>
      )}
      <div className="order-6 mt-3 flex gap-2">
        <Input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={agentOn ? "Örn. Merkez mağazaya 20 paket karton bardak taslak siparişi oluştur" : "Örn. Bugün en çok ne sattık?"} className="flex-1" disabled={busy} />
        {agentCan && agentCfg?.enabled && (
          <Button data-testid="ai-agent-toggle" onClick={() => setAgentMode((v) => !v)} variant="outline"
            title="Otonom mod: düşük riskli işleri AI otomatik yapar (Kesin Duvar korumalı)"
            className={agentMode ? "border-brand-orange bg-brand-orange/10 text-brand-orange" : "text-slate-500"}>
            <Cpu className="h-4 w-4" />
          </Button>
        )}
        <Button data-testid="ai-mic" onClick={toggleMic} disabled={busy || transcribing} variant="outline"
          title="Sesle sor" className={recording ? "animate-pulse border-red-400 text-red-600" : ""}>
          {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
        {canSuggest && !agentOn && (
          <Button data-testid="ai-suggest-btn" onClick={askSuggest} disabled={busy || !input.trim()} variant="outline" title="Bu istekten ürün/fiyat önerisi üret (onayınla uygula)" className="border-brand-green text-brand-green hover:bg-brand-green/10">
            <Wand2 className="h-4 w-4" />
          </Button>
        )}
        <Button data-testid="ai-send" onClick={() => send()} disabled={busy || !input.trim()} className={agentOn ? "bg-brand-orange text-white hover:bg-brand-orange/90" : "bg-brand-green text-white hover:bg-brand-green-600"}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
      <p className="order-7 mt-1.5 text-center text-[11px] text-slate-400">{agentOn ? "Otonom mod: AI yalnız düşük riskli işleri yapar; her işlem 'Eylemler' sekmesinden geri alınabilir. Kritik alanlar Kesin Duvar ardındadır." : "AI yalnızca bilgi/analiz sunar, işlem yapmaz. Yanıtlar hata içerebilir; kritik kararlarda verileri doğrulayın."}</p>
    </div>
  );
}
